import { publicationStateLabel } from "@/lib/projectStatus";
import { Badge } from "@/ui/Badge";
import type {
  ClinicianPublicationState,
  ClinicianPublicationStatus,
  LifecycleClassification,
  PublicationAttentionReason,
} from "@/api/publication";

/**
 * The compact clinician publication status (#267), rendered (#9).
 *
 * This is the *primary* status the amended Project UX asks for, and its whole
 * vocabulary is the four members of `ClinicianPublicationState`. There is no
 * second reading composed here from anything else: no check name, no PR link,
 * no head SHA, no member of `GitLifecycleState`. None of those is even
 * representable on the projection this renders, which is what makes #9's
 * criterion — "raw candidate/PR/SHA/check vocabulary is absent from the primary
 * clinician status" — hold for states nobody thought to sample. #788's explicit
 * Copy PR link action is a separate workflow control backed by a
 * `PublicationRecord`; it does not add repository vocabulary to this reading.
 *
 * `publication_requested` covers every rung a live publication can be on,
 * including merged, published and handed off. That is deliberate and must not
 * be "improved" into a more precise reading here: the Editor observes a packs
 * repository and never a tenant's runtime, so the fine-grained rung belongs to
 * `GET .../git-lifecycle` and #268's support surface. Nothing in this file may
 * say deployed, promoted or live, because the Editor cannot know any of them.
 */
/*
  THE LABELS MOVED, and only the labels. They now live in
  `lib/projectStatus.ts`, unchanged, because #439 renders this same projected
  value on the Projects index: `ProjectListItem.publicationStatus` exists so
  that "an index row and the page it opens can never disagree", and two tables
  for one enum is precisely how they would disagree in words while agreeing in
  data. `action_needed` is still rendered on the badge like the other three, and
  still paired with the recovery sentence its `reason` selects rather than with
  the progress line the other states carry — the primary surface holds ONE
  status, and "Accepted" sitting beside a failure would be telling a clinician
  two things about one project and leaving them to decide which is current.

  The TONE stays here. It is a property of this surface's badge, not of the
  vocabulary: the index draws its second badge in the canonical subtle style
  whatever the state is.
*/
const STATE_TONE = {
  none: "neutral",
  accepted: "success",
  publication_requested: "accent",
  action_needed: "warning",
} satisfies Record<
  ClinicianPublicationState,
  "neutral" | "success" | "accent" | "warning"
>;

/**
 * What each recovery actually asks of the clinician.
 *
 * The contract chose these member names as clinician vocabulary on purpose — the
 * member for a failed check is not called `checks_failed`, and the one for a
 * moved head is not called `approval_invalidated` — and the sentences below keep
 * that promise. Each says what happened and what to do, in that order, without
 * naming a repository, a pull request, a check or a SHA.
 */
const REASON_COPY = {
  publication_did_not_complete:
    "The publication did not finish. Nothing was lost — you can request it again.",
  automated_review_failed:
    "Automated review found problems with this version. Revise the clinical content, then freeze and preview a new version.",
  signoff_no_longer_current:
    "The publication review is no longer current because this version changed. It must be reviewed again before publication can continue.",
} satisfies Record<PublicationAttentionReason, string>;

/**
 * The provisional stamp, carried rather than recomputed.
 *
 * A #204 trial publication reads `provisional-v0 · non-evidence ·
 * non-promotable` on every rung, including a merged one, and the projection
 * carries that classification verbatim precisely so a surface cannot quietly
 * present a trial as the real thing. Rendering it beside the status is that
 * protection reaching the screen.
 *
 * `classification.stamp` is rendered VERBATIM rather than composed here. The
 * contract calls it "the composed, display-ready form of the fields above" and
 * pins the trial wording to that exact string; joining the three fields locally
 * would be a second composition of one fact, and the two would disagree the
 * first time either side changed a separator or a term. Only the DECISION to
 * show it reads the semantic fields — a stamp is shown when the lifecycle is
 * anything less than evidence-bearing and promotable, which is the fail-closed
 * direction: an unrecognised value stamps rather than stays silent.
 */
function ProvisionalStamp({
  state,
  classification,
}: {
  state: ClinicianPublicationState;
  classification: LifecycleClassification;
}) {
  // Nothing has been accepted or published, so there is no publication for a
  // classification to be ABOUT. The projection still carries one — the
  // fail-closed default, `non-evidence · non-promotable` — and rendering it here
  // told every untouched Project it held something provisional. Measured in the
  // browser against a real service, not theorised: a Project created seconds
  // earlier read "Not started · non-evidence · non-promotable".
  //
  // Suppressed on `none` ALONE. The stamp's job is to stop a #204 trial
  // publication reading as the real thing, and a trial publication is never in
  // this state — it has a publication row by definition — so nothing the stamp
  // exists to catch is hidden by this.
  if (state === "none") {
    return null;
  }

  const provisional =
    classification.evidenceClass !== "evidence-bearing" ||
    classification.promotability !== "promotable";

  if (!provisional) {
    return null;
  }

  return <Badge tone="outline">{classification.stamp}</Badge>;
}

/**
 * One status line, and never two.
 *
 * `action_needed` REPLACES the progress reading rather than sitting beside it,
 * which is the projection's own rule: a surface showing "Accepted" next to a
 * failure is telling a clinician two things about one project and leaving them
 * to decide which is current.
 *
 * ONE STATUS, AND NO ACTOR. `state` is the publication axis and carries no
 * subject: it says a version is accepted, never WHO accepted it. The sentence
 * below used to say "You have accepted this version", which was a guess — the
 * only axis that knows the actor is `candidateAcceptance`'s `acceptedBy` (#431),
 * and it is frequently somebody else. The composed page then said "You have
 * accepted this version" directly above "This version was accepted by another
 * clinician", telling a clinician they personally recorded a clinical
 * acceptance they did not make. So actor identity lives in exactly one place,
 * `CandidateAcceptanceDetail`, which reads the axis that actually carries it;
 * this sentence states the standing and stays silent about the subject.
 */
export function PublicationStatusView({
  status,
}: {
  status: ClinicianPublicationStatus;
}) {
  const needsAction = status.state === "action_needed";
  // The contract guarantees `reason` is present if and only if the state is
  // `action_needed`, and enforces it server-side with a model validator. The
  // fallback exists anyway: generated types describe a contract, they do not
  // validate a response, and a status with no sentence would be a dead end.
  const recovery = needsAction
    ? (status.reason ? REASON_COPY[status.reason] : undefined) ??
      "This publication needs your attention. Open the support detail below for the current record."
    : undefined;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={STATE_TONE[status.state]}>
          {publicationStateLabel(status.state)}
        </Badge>
        <ProvisionalStamp
          state={status.state}
          classification={status.classification}
        />
      </div>

      {recovery ? (
        <p className="text-sm text-foreground">{recovery}</p>
      ) : (
        <p className="text-sm text-muted-foreground">
          {status.state === "none"
            ? "Nothing has been accepted for publication yet."
            : status.state === "accepted"
              ? "This version has been accepted. Request publication to create its Clinical Pack PR for review."
              : "Publication has been requested. The project's release journey shows GitHub approval and deployment status."}
        </p>
      )}
    </div>
  );
}
